import React, { ReactNode } from 'react';
import moment from 'moment';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';
import { SectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import ArrowRightIcon from 'packages/kakapo-ui-kit/assets/icons/ArrowRightIcon';
import ArrowLeftIcon from 'packages/kakapo-ui-kit/assets/icons/ArrowLeftIcon';
import LearningCalendarWeekdayHeader from 'packages/kakapo-ui-application/pages/learning/components/calendar/LearningCalendarWeekdayHeader';
import { calendarEventDayKey } from 'packages/kakapo-ui-application/pages/learning/types/CalendarEvent';
import LearningCalendarWeek from 'packages/kakapo-ui-application/pages/learning/components/calendar/LearningCalendarWeek';
import { useTranslation } from 'react-i18next';

const weeksInMonth = (year: number, month: number): number[] => {
	const firstDayOfMonth = moment(`${year}-${month}`, 'YYYY-MM-DD');
	const daysInMonth = firstDayOfMonth.daysInMonth();
	const weeks = new Set<number>();

	for (let i = 0; i < daysInMonth; i += 1) {
		const currentDay = moment(firstDayOfMonth, 'YYYY-MM-DD').add(i, 'days');

		// Only add weeks where work days are present.
		if (currentDay.isoWeekday() !== 6 && currentDay.isoWeekday() !== 7) {
			weeks.add(currentDay.isoWeek());
		}
	}

	return Array.from(weeks);
};

const LearningCalendarElement = styled.div`
	background-color: ${(props) => props.theme.colors.lightBlueLight};
	border: 1px solid ${(props) => props.theme.colors.lightBlue};
	border-radius: 6px;
`;

const LearningCalendarArrowLinkElement = styled(Box)`
	button {
		display: block;
		margin: 0;
		background-color: transparent;
	}

	svg {
		display: block;
		transform: translateY(-1px);
	}

	button:hover {
		svg {
			path {
				fill: ${(props) => props.theme.colors.primary};
			}
		}
	}
`;

const AttendanceIndicator = styled.div<{ filled?: boolean }>`
	width: 30px;
	height: 20px;
	border: 1px solid ${(props) => props.theme.colors.slateBlue};
	border-radius: 3px;
	margin-top: -2px;

	${(props) => (props.filled ? `background-color: ${props.theme.colors.slateBlue};` : '')}
`;

const CalendarLegend = styled(Flex)`
	${AttendanceIndicator}, span {
		margin-left: 10px;
		color: ${(props) => props.theme.colors.slateBlue};
	}
`;

const LearningCalendarArrowLink = ({ icon, onClick }: { icon: ReactNode; onClick: () => void }) => (
	<LearningCalendarArrowLinkElement ml={2}>
		<button type="button" onClick={onClick}>
			{icon}
		</button>
	</LearningCalendarArrowLinkElement>
);

const LearningCalendar = ({
	events,
	year,
	month,
	onNext,
	onPrev,
}: {
	events: graphql.LearningEvent[];
	year: number;
	month: number;
	onNext: () => void;
	onPrev: () => void;
}) => {
	const { t } = useTranslation();

	const monthMoment = moment(`${year}-${month}`, 'YYYY-MM');

	const eventsByDateString: Record<string, graphql.LearningEvent[]> = events.reduce(
		(result, event) => {
			const key = calendarEventDayKey(event.startDate);

			return {
				...result,
				[key]: result[key]?.concat([event]) ?? [event],
			};
		},
		{} as Record<string, graphql.LearningEvent[]>,
	);

	return (
		<>
			<LearningCalendarElement>
				<Flex mx={3} my="12px" justifyContent="space-between" alignItems="center">
					<SectionTitle>
						<strong>{monthMoment.format('MMMM')}</strong>
						&nbsp;
						{monthMoment.format('YYYY')}
					</SectionTitle>

					<Flex>
						<LearningCalendarArrowLink onClick={onPrev} icon={<ArrowLeftIcon />} />
						<LearningCalendarArrowLink onClick={onNext} icon={<ArrowRightIcon />} />
					</Flex>
				</Flex>

				<LearningCalendarWeekdayHeader />

				{weeksInMonth(year, month).map((week) => (
					<LearningCalendarWeek
						eventsByDateString={eventsByDateString}
						year={year}
						month={month}
						week={week}
						key={`LearningCalendarWeek-${week}`}
					/>
				))}
			</LearningCalendarElement>

			<Box mt={3}>
				<CalendarLegend justifyContent="flex-end" alignItems="center">
					<AttendanceIndicator />
					<span>{t('learning.calendar.legend.notAttending')}</span>
					<AttendanceIndicator filled />
					<span>{t('learning.calendar.legend.attending')}</span>
				</CalendarLegend>
			</Box>
		</>
	);
};

export default LearningCalendar;
